// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Dictionary } from 'typescript-collections';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { Vector3 } from 'three';
import { BaseAdvancedOperation } from '@shared/undo/operation/base-advanced-operation';
import { UndoInsertArticlesByLocationOperation } from '@shared/undo/operation/undo-insert-articles-by-location-operation';
import { UndoReplaceArticlesByLocationOperation } from '@shared/undo/operation/undo-replace-articles-by-location-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { AlertHelper } from '@shared/alert/alert.helper';
import { InsertArticleUtils } from '../util/insert-article-utils';

export abstract class BaseArticleManager {

  protected _articleLocation: ArticleLocationVO;
  protected _detachOperation: UndoDetachArticlesOperation;

  constructor(
    protected projectModel: ProjectModel,
    private undoRedoHelper: UndoRedoHelper,
    private alertHelper: AlertHelper,
    protected undoRedoUtils: UndoRedoUtils,
    private insertArticleUtils: InsertArticleUtils
  ) {}

  public abstract recreateArticle(articleUsage: ArticleUsageVO, length: number, offset: number): Promise<void>;

  protected createOnRailArticleLocation(parentUsage: ArticleUsageVO, articleOffset: number): OnRailReferenceArticleLocationVO {
    const articleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
    articleLocation.rangeOnLineArticleUsage = parentUsage;
    articleLocation.articleOffset = articleOffset;
    articleLocation.checkConnectionProperties = false;
    return articleLocation;
  }

  protected getParentArticleUsage(articleUsage: ArticleUsageVO): ArticleUsageVO {
    const parentUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
    return parentUsageIds.length === 1 ? this.projectModel.getArticleUsage(parentUsageIds[0]) : null;
  }

  protected async pushInsertArticleOperation(insertedGUIDs: string[],
                                       moveMap: Dictionary<string, Vector3>,
                                       additionalOperations: IOperation[] = null): Promise<void> {
    const attachOperation: UndoAttachArticlesOperation =
    this.undoRedoUtils.createUndoAttachOperationByUsageIds(insertedGUIDs);
    if (!attachOperation) {
      return;
    }
    let undoOperation: BaseAdvancedOperation = null;
    if (!this._detachOperation) {
      undoOperation = new UndoInsertArticlesByLocationOperation(attachOperation, moveMap, this.insertArticleUtils);
    } else {
      undoOperation = new UndoReplaceArticlesByLocationOperation(attachOperation, this._detachOperation, moveMap, this.insertArticleUtils);
    }
    undoOperation.additionalOperations = additionalOperations;
    await this.undoRedoHelper.pushUndoOperation(undoOperation);
  }

  protected reset(): void {
    this._articleLocation = null;
    this._detachOperation = null;
  }

  protected async finalizeFailureProcessingArticle(errorMessage: string, errorTitle: string): Promise<void> {
    this.alertHelper.showError(errorTitle, errorMessage);
    await this.revertRemovedArticles();
    this.reset();
  }

  protected async revertRemovedArticles(): Promise<void> {
    if (!this._detachOperation) {
      return;
    }
    await this._detachOperation.performUndo();
  }
}
